import { ChangeEvent, Dispatch, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { fetchIsValidPostalCode } from '../api/httpHandlers'
import { AppAction } from '../appState'
import { track } from '../lib/tracking'
import { camelToKebab } from '../lib/utils'

interface Props {
  initialValue: string
  appDispatch: Dispatch<AppAction>
  id: string
  validationFunction: (value: string) => string
  formatFunction?: (value: string) => string
  maxLength?: number
}

export default function useForm({
  initialValue,
  appDispatch,
  id,
  validationFunction,
  formatFunction = (value) => (value ? new Intl.NumberFormat('sv-SE').format(Number(value)) : ''),
  maxLength = 10,
}: Props) {
  const [value, setValue] = useState(initialValue)
  const [errorMessage, setErrorMessage] = useState('')
  const [hasBeenTouched, setHasBeenTouched] = useState(initialValue !== '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (window.innerWidth > 1024) {
      inputRef.current?.focus()
    }
  }, [inputRef])
  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.replace(/\D+/g, '')
      if (trimmedValue.length > maxLength) return
      if (trimmedValue === value) return
      setHasBeenTouched(true)
      setErrorMessage('')
      track('user-input', camelToKebab(id))
      appDispatch({ type: 'inputValueChange', value: trimmedValue })
      setValue(trimmedValue)
    },
    [maxLength, value, id, appDispatch],
  )

  const validate = useCallback(() => {
    const newErrorMessage = validationFunction(value)
    setErrorMessage(newErrorMessage)
    return !newErrorMessage
  }, [value, validationFunction])

  const submitHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      async function validatePostalCode() {
        try {
          const result = await fetchIsValidPostalCode(value)
          if (result.successful && result.isValid) {
            appDispatch({ type: 'setIsValidatingPostalCode', value: false })
            appDispatch({ type: 'goForward' })
          } else {
            appDispatch({ type: 'setIsValidatingPostalCode', value: false })
            setErrorMessage('Välj ett giltigt postnummer')
          }
        } catch (err) {
          appDispatch({ type: 'setHasAppError', value: true })
        }
      }
      event.preventDefault()
      track('click', `next-button.${camelToKebab(id)}`)
      if (!validate()) return
      if (id === 'postal_code') {
        appDispatch({ type: 'setIsValidatingPostalCode', value: true })
        validatePostalCode().catch(() => {
          appDispatch({ type: 'setHasAppError', value: true })
        })
      } else {
        appDispatch({ type: 'goForward' })
      }
    },
    [id, validate, value, appDispatch],
  )

  const formattedValue = formatFunction(value)

  return {
    inputRef,
    formattedValue,
    errorMessage,
    hasBeenTouched,
    onChangeHandler,
    submitHandler,
    validate,
  }
}
